// window.mytheseus.calendar.subscribe(listener) → unsubscribe: tells the
// app, in this page, that it has just lost Google Calendar access, so it
// can clear what it shows and caches straight away instead of at its next
// read. Only a server-verified answer counts: a disconnect the server
// confirmed (from the App settings panel or the app's own disconnect()),
// a status read that says disconnected after one that said connected, or
// this device losing its access to the app (the page's last event).
// The listener gets a frozen {connected: false, revocation_pending}; never
// tokens, scopes or calendar data. A failed or ambiguous read never emits.
// Treat it as idempotent: overlapping reads can deliver it twice.

export function createCalendarSignal({ onError = (e) => console.error(e) } = {}) {
  const listeners = new Set();
  let known = 'unknown'; // what this page last verified: connected | disconnected | unknown
  let disposed = false;
  let revision = 0;

  function notify(payload) {
    for (const entry of [...listeners]) {
      if (!listeners.has(entry)) continue; // unsubscribed by an earlier listener
      try { entry.listener(payload); } catch (e) { onError(e); }
    }
  }

  function disconnected(status) {
    if (disposed) return;
    ++revision; // All reads begun before this confirmed write are obsolete.
    if (known === 'disconnected') return;
    known = 'disconnected';
    notify(Object.freeze({ connected: false, revocation_pending: status?.revocation_pending === true }));
  }

  return {
    subscribe(listener) {
      if (typeof listener !== 'function') throw new TypeError('calendar.subscribe(listener) needs a function');
      if (disposed) return () => {};
      const entry = { listener };
      listeners.add(entry);
      return () => { listeners.delete(entry); };
    },
    /** A status answer from the server: {connected, revocation_pending?}. */
    revision: () => revision,
    observe(status, startedAt = revision) {
      if (disposed || startedAt !== revision) return;
      if (status?.connected === true) known = 'connected';
      else if (status?.connected === false && known === 'connected') disconnected(status);
    },
    /** The server confirmed this app's grant is gone. */
    disconnected,
    /** This page's runtime is going away (the device lost its access): the
     *  app can no longer read Calendar, so say so once, then drop every listener. */
    dispose() {
      if (disposed) return;
      if (known !== 'disconnected') { known = 'disconnected'; notify(Object.freeze({ connected: false, revocation_pending: false })); }
      disposed = true;
      listeners.clear();
    },
    size: () => listeners.size,
  };
}
